'use client';

import { IcGrid2, IcGrid3, IconButton, Switch, Typography } from '@musinsa/mds';
import { SERVICE, useDeviceInfo } from '@musinsa/packages-device-detection';
import { track } from '@musinsa/packages-event-snowplow';
import { parseAsBoolean, parseAsStringLiteral, useQueryState } from '@musinsa/packages-nuqs';
import type { RecruitmentSort } from '@musinsa/services-curator';
import { useQuery } from '@tanstack/react-query';
import { curatorQueries } from '../../query-factory';
import { SNOWPLOW_SCHEME } from '../../snowplow';
import { isMusinsaService } from '../utils/campaignProposalMusinsaAnalytics';
import {
  trackSeedingListSortFilterClickAmplitude,
  trackSeedingListToggleFilterClickAmplitude,
} from '../utils/seedingMusinsaAnalytics';
import { DropDownMenu } from './DropDownMenu';

export const LIKED_BRANDS_QUERY_KEY = 'likedBrandsOnly';
export const SORT_QUERY_KEY = 'sort';
export const COLUMNS_QUERY_KEY = 'columns';

const COLUMNS_VALUES = ['2', '3'] as const;
export type GridColumns = (typeof COLUMNS_VALUES)[number];

const SORT_OPTIONS: ReadonlyArray<{ label: string; value: RecruitmentSort }> = [
  { label: '최신순', value: 'LATEST' },
  { label: '선정 확률 높은 순', value: 'SELECTION_PROBABILITY' },
  { label: '종료 임박 순', value: 'DEADLINE_SOON' },
];

const SORT_VALUES = SORT_OPTIONS.map(({ value }) => value);

export const useLikedBrandsOnly = () =>
  useQueryState(
    LIKED_BRANDS_QUERY_KEY,
    parseAsBoolean.withDefault(false).withOptions({ history: 'replace' }),
  );

export const useRecruitmentSort = () =>
  useQueryState(
    SORT_QUERY_KEY,
    parseAsStringLiteral(SORT_VALUES).withDefault('LATEST').withOptions({ history: 'replace' }),
  );

export const useRecruitmentColumns = () =>
  useQueryState(
    COLUMNS_QUERY_KEY,
    parseAsStringLiteral(COLUMNS_VALUES).withDefault('3').withOptions({ history: 'replace' }),
  );

export const CampaignRecruitmentFilterRow = () => {
  const [likedBrandsOnly, setLikedBrandsOnly] = useLikedBrandsOnly();
  const [sort, setSort] = useRecruitmentSort();
  const [columns, setColumns] = useRecruitmentColumns();
  const { service } = useDeviceInfo();
  // curatorId — 29CM Snowplow layout_id 슬롯. react-query dedupe 로 추가 네트워크 없음.
  const { data: userInfo } = useQuery({ ...curatorQueries.profile.userInfo });
  const curatorId = userInfo?.snsTitle ?? '';
  const sortIndex = Math.max(
    0,
    SORT_OPTIONS.findIndex(({ value }) => value === sort),
  );
  const nextColumns: GridColumns = columns === '2' ? '3' : '2';
  const GridIcon = columns === '2' ? IcGrid2 : IcGrid3;

  const is29CM = service === SERVICE['29CM'];

  // 시딩 캠페인 리스트 > 토글 필터(좋아요한 브랜드) 클릭
  // 29CM: Snowplow / 무신사: Amplitude (amp-seeding-list.md no.3)
  const handleLikedToggle = (next: boolean) => {
    if (is29CM) {
      track({
        schema: SNOWPLOW_SCHEME.CLICK_BUTTON,
        data: {
          page_name: 'curator_seeding_campaign_list',
          section_name: 'CuratorCatogoryFilter',
          section_title: 'CuratorCatogoryFilter',
          button_id: 'curator_seeding_toggle_filter',
          button_name: 'liked_brand',
          toggle: next ? 'on' : 'off',
          position: -1,
          layout_id: curatorId,
        },
      });
    }
    if (isMusinsaService(service)) {
      trackSeedingListToggleFilterClickAmplitude({ toggle: next ? 'on' : 'off' });
    }
    setLikedBrandsOnly(next ? true : null);
  };

  // 시딩 캠페인 리스트 > 정렬 필터 클릭
  // 29CM: Snowplow / 무신사: Amplitude (amp-seeding-list.md no.4)
  const handleSortSelect = (value: RecruitmentSort) => {
    const label = SORT_OPTIONS.find((o) => o.value === value)?.label ?? value;
    if (is29CM) {
      track({
        schema: SNOWPLOW_SCHEME.CLICK_BUTTON,
        data: {
          page_name: 'curator_seeding_campaign_list',
          section_name: 'CuratorCatogoryFilter',
          section_title: 'CuratorCatogoryFilter',
          button_id: 'curator_seeding_sort_filter',
          button_name: label,
          position: -1,
          layout_id: curatorId,
        },
      });
    }
    if (isMusinsaService(service)) {
      trackSeedingListSortFilterClickAmplitude({ filterName: label });
    }
    setSort(value);
  };

  return (
    <div className="flex items-center justify-between bg-gray-100 px-[16px]! py-[12px]!">
      <div className="flex items-center gap-[4px] mr-[20px]!">
        <Switch
          size="24x16"
          checked={likedBrandsOnly}
          onCheckedChange={(next) => handleLikedToggle(Boolean(next))}
        />
        <Typography
          variant="body13pxReg"
          colorScheme="gray-600"
          onClick={() => handleLikedToggle(!likedBrandsOnly)}
          className="cursor-pointer"
        >
          좋아요한 브랜드
        </Typography>
      </div>

      <div className="flex items-center gap-[8px]">
        <DropDownMenu
          items={[...SORT_OPTIONS]}
          selectedIndex={sortIndex}
          onSelect={(value) => handleSortSelect(value as RecruitmentSort)}
        />
        <IconButton
          size="size16x16"
          aria-label={`${nextColumns}열 보기`}
          onClick={() => setColumns(nextColumns === '3' ? null : nextColumns)}
        >
          <GridIcon strokeColor="gray-600" />
        </IconButton>
      </div>
    </div>
  );
};
